import PropTypes from 'prop-types'
import { useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { ExpandCircleDownOutlined } from '@mui/icons-material'

import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'

import { color } from 'styles/colors'

const ANIMATION_TIME = window.runningSpecs ? 0 : 400

const propTypes = {
  open: PropTypes.bool,
  headerComponent: PropTypes.node.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  caretVerticalPosition: PropTypes.string,
  caretHorizontalPosition: PropTypes.string,
  caretSize: PropTypes.string,
  expandedCallback: PropTypes.func,
  collapsedCallback: PropTypes.func,
  onClickCallback: PropTypes.func,
  className: PropTypes.string
}

const defaultProps = {
  open: false,
  caretVerticalPosition: 'center',
  caretHorizontalPosition: 'left',
  caretSize: '24px',
  className: null,
  children: null,
  onClickCallback: () => {},
  expandedCallback: () => {},
  collapsedCallback: () => {}
}

const CollapsiblePanel = ({
  open,
  headerComponent,
  caretVerticalPosition,
  caretHorizontalPosition,
  caretSize,
  children,
  expandedCallback,
  onClickCallback,
  collapsedCallback,
  className
}) => {
  const [isOpen, setIsOpen] = useState(open)
  const panelContentRef = useRef(null)

  const caretIconName = () => {
    if (caretHorizontalPosition === 'right') {
      return isOpen ? 'up3' : 'down3'
    } else {
      return isOpen ? 'down3' : 'right3'
    }
  }

  const onClick = useCallback(() => {
    if (panelContentRef.current && isOpen) {
      $(panelContentRef.current).slideUp(ANIMATION_TIME)

      collapsedCallback()
    } else if (panelContentRef.current && !isOpen) {
      $(panelContentRef.current).slideDown(ANIMATION_TIME)

      expandedCallback()
    }

    onClickCallback()

    setIsOpen(!isOpen)
  }, [isOpen, expandedCallback, onClickCallback, collapsedCallback])

  return (
    <S.CollapsiblePanel {...{ className }}>
      <S.Header tabIndex={0} {...{ onClick, caretHorizontalPosition }}>
        <Tooltip title={isOpen ? 'Click to minimize' : 'Click to expand'} placement="top">
          <S.CaretIcon
            {...{ caretVerticalPosition, caretSize }}
            className={`icon-arrow-${caretIconName()}`}
            isOpen={isOpen}
          />
        </Tooltip>
        <S.HeaderComponent>{headerComponent}</S.HeaderComponent>
      </S.Header>
      <S.Content {...{ isOpen }} ref={panelContentRef}>
        {children}
      </S.Content>
    </S.CollapsiblePanel>
  )
}

CollapsiblePanel.propTypes = propTypes
CollapsiblePanel.defaultProps = defaultProps

export default CollapsiblePanel

const S = {
  CollapsiblePanel: styled.div`
    background-color: inherit;
  `,
  Header: styled(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['caretVerticalPosition', 'caretHorizontalPosition'].includes(prop)
  })`
    display: flex;
    cursor: pointer;
    flex-direction: ${({ caretHorizontalPosition }) => caretHorizontalPosition === 'left' ? 'row' : 'row-reverse'};
  `,
  HeaderComponent: styled.div`
    flex-grow: 1;
    overflow: hidden;
  `,
  Content: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isOpen'].includes(prop)
  })`
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  `,
  CaretIcon: styled(ExpandCircleDownOutlined).withConfig({
    shouldForwardProp: (prop) => !['caretSize', 'caretVerticalPosition', 'isOpen'].includes(prop)
  })`
    display: block;
    font-size:  ${({ caretSize }) => caretSize};
    align-self: ${({ caretVerticalPosition }) => caretVerticalPosition};
    color: ${color('actionableIcon')};
    margin: 0px 10px;
    transform: ${({ isOpen }) => !isOpen && 'rotate(-90deg);'}
  `
}
